import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { detectTenant } from '../config/tenantConfig';
import { getPlatformFromTenant, canSeeSidebarItem, hasRequiredRole, isPlatformAdmin, getUserType, getUserSubRole, USER_TYPES } from './permissionsConfig';
import api from '../lib/api';

const RoleContext = createContext(null);

export const RoleProvider = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState(user);
  const [dbPermissions, setDbPermissions] = useState(null);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  useEffect(() => {
    setCurrentUser(user);
  }, [user]);

  const tenant = detectTenant(location.pathname);
  const platform = useMemo(() => getPlatformFromTenant(tenant), [tenant]);

  // Load permissions from database
  useEffect(() => {
    const loadDbPermissions = async () => {
      if (!platform || !user) {
        setPermissionsLoaded(true);
        return;
      }
      try {
        const res = await api.get(`/api/permissions/load?platform=${platform}`);
        if (res.data?.success && res.data?.data?.permissions) {
          setDbPermissions(res.data.data.permissions);
        }
      } catch (error) {
        console.log('[RoleContext] Could not load DB permissions, using defaults');
      } finally {
        setPermissionsLoaded(true);
      }
    };
    loadDbPermissions();
  }, [platform, user]);

  // Check if user has access using DB permissions if available
  const hasAccessTo = useCallback(
    (itemKey) => {
      if (!itemKey) return true;
      if (!currentUser) return false;

      // If DB permissions are loaded, use them
      if (dbPermissions) {
        const userType = getUserType(currentUser);

        if (userType !== USER_TYPES.PLATFORM) {
          // Check customer/agent permissions
          const allowedItems = dbPermissions[userType];
          if (Array.isArray(allowedItems) && allowedItems.length > 0) {
            return allowedItems.includes(itemKey);
          }
        } else {
          // Check platform user permissions by sub-role
          const subRole = getUserSubRole(currentUser);
          const allowedItems = dbPermissions[subRole];
          if (Array.isArray(allowedItems)) {
            return allowedItems.includes(itemKey);
          }
        }
      }

      // Fall back to default static permissions
      return canSeeSidebarItem(currentUser, platform, itemKey);
    },
    [currentUser, platform, dbPermissions]
  );

  const value = useMemo(() => ({
    user: currentUser,
    setUser: setCurrentUser,
    platform,
    hasAccessTo,
    hasRequiredRole: (requiredRole) => hasRequiredRole(requiredRole, currentUser, platform),
    isPlatformAdmin: () => isPlatformAdmin(currentUser),
    permissionsLoaded
  }), [currentUser, platform, hasAccessTo, permissionsLoaded]);

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};

export const useRoleContext = () => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRoleContext must be used within a RoleProvider');
  }
  return context;
};

export default RoleContext;
